import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { ChevronRight, ChevronDown, Send, Volume2, Square, Sparkles, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const stripMd = (t) =>
  (t || "")
    .replace(/\[Source:[^\]]*\]/g, "")
    .replace(/[#*_`>-]/g, "")
    .replace(/\n+/g, ". ")
    .replace(/\s+/g, " ")
    .trim();

const splitSources = (t) => {
  const sources = [...(t || "").matchAll(/\[Source:\s*([^\]]*)\]/g)].map((m) => m[1].trim());
  const body = (t || "").replace(/\[Source:[^\]]*\]/g, "").replace(/[ \t]+\n/g, "\n").trim();
  return { body, sources: [...new Set(sources)].filter(Boolean) };
};

function Answer({ text }) {
  const { body, sources } = splitSources(text);
  return (
    <>
      <div className="chat-markdown space-y-2 text-sm leading-relaxed text-brand-ink/80">
        <ReactMarkdown>{body}</ReactMarkdown>
      </div>
      {sources.length > 0 && (
        <p className="mt-2 text-xs text-brand-muted">
          Sources: <span className="text-brand-sage">{sources.join(" · ")}</span>
        </p>
      )}
    </>
  );
}

export default function AskAyurvedam() {
  const qc = useQueryClient();
  const { data: faq = [] } = useQuery({
    queryKey: ["qa-faq"],
    queryFn: () => fetch(`${API}/qa/faq`).then((r) => r.json()),
  });

  const [openId, setOpenId] = useState(null);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null); // { question, answer, cached }
  const [error, setError] = useState("");
  const [speakingId, setSpeakingId] = useState(null);
  const supportsTTS = typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => () => { if (supportsTTS) window.speechSynthesis.cancel(); }, [supportsTTS]);

  const speak = (id, text) => {
    if (!supportsTTS) return;
    if (speakingId === id) {
      window.speechSynthesis.cancel();
      setSpeakingId(null);
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(stripMd(text));
    u.rate = 0.98;
    u.onend = () => setSpeakingId(null);
    u.onerror = () => setSpeakingId(null);
    setSpeakingId(id);
    window.speechSynthesis.speak(u);
  };

  const ask = async (e) => {
    e.preventDefault();
    const q = input.trim();
    if (q.length < 3 || asking) return;
    setAsking(true);
    setError("");
    setAnswer(null);
    try {
      const res = await fetch(`${API}/qa/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Something went wrong.");
      setAnswer(data);
      setInput("");
      qc.invalidateQueries({ queryKey: ["qa-faq"] });
    } catch (err) {
      setError(err.message || "Could not get an answer. Please try again.");
    } finally {
      setAsking(false);
    }
  };

  return (
    <section data-testid="ask-ayurvedam" className="border-t border-brand-line bg-brand-mist/40">
      <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8 sm:py-20">
        <div className="flex items-center gap-2 text-brand">
          <Sparkles className="h-5 w-5" />
          <p className="text-xs font-semibold uppercase tracking-[0.2em]">Knowledge base</p>
        </div>
        <h2 className="mt-3 flex items-center gap-2 font-heading text-4xl text-brand-ink tracking-tight sm:text-5xl">
          Ask Ayurvedam.com <ChevronRight className="h-8 w-8 text-brand-sage" />
        </h2>
        <p className="mt-3 max-w-2xl text-brand-muted">
          Common questions answered from our reviewed Ayurveda content. Can't find yours? Ask below —
          our assistant answers instantly and saves it here for everyone.
        </p>

        {/* Ask form */}
        <form onSubmit={ask} data-testid="ask-form" className="mt-8 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="ask-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question about Ayurveda, herbs or therapies…"
            className="flex-1 rounded-full border border-brand-line bg-white px-5 py-3.5 text-sm text-brand-ink outline-none transition-colors focus:border-brand"
          />
          <button
            data-testid="ask-submit"
            type="submit"
            disabled={asking || input.trim().length < 3}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-medium text-brand-sand transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
          >
            {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {asking ? "Thinking…" : "Ask"}
          </button>
        </form>

        {error && <p data-testid="ask-error" className="mt-3 text-sm text-brand-clay">{error}</p>}

        {/* Fresh answer */}
        {answer && (
          <div data-testid="ask-answer" className="mt-6 rounded-2xl border border-brand bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-brand text-brand-sand">
                  <Sparkles className="h-3.5 w-3.5" />
                </span>
                <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-medium text-brand">
                  {answer.cached ? "From our knowledge base" : "New answer · saved"}
                </span>
              </div>
              {supportsTTS && (
                <button
                  data-testid="ask-answer-listen"
                  onClick={() => speak("fresh", answer.answer)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand hover:border-brand"
                >
                  {speakingId === "fresh" ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                  {speakingId === "fresh" ? "Stop" : "Listen"} <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
            <h3 className="mt-3 font-heading text-lg text-brand-ink">{answer.question}</h3>
            <div className="mt-2">
              <Answer text={answer.answer} />
            </div>
          </div>
        )}

        {/* FAQ accordion */}
        <div className="mt-10 divide-y divide-brand-line rounded-2xl border border-brand-line bg-white">
          {faq.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div key={item.id} data-testid={`faq-item-${item.id}`}>
                <button
                  data-testid={`faq-question-${item.id}`}
                  onClick={() => { setOpenId(isOpen ? null : item.id); if (isOpen && speakingId === item.id) speak(item.id, ""); }}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-brand-mist/40"
                >
                  <span className="font-medium text-brand-ink">{item.question}</span>
                  {isOpen ? <ChevronDown className="h-5 w-5 shrink-0 text-brand-sage" /> : <ChevronRight className="h-5 w-5 shrink-0 text-brand-muted" />}
                </button>
                {isOpen && (
                  <div data-testid={`faq-answer-${item.id}`} className="px-5 pb-5">
                    <Answer text={item.answer} />
                    {supportsTTS && (
                      <button
                        data-testid={`faq-listen-${item.id}`}
                        onClick={() => speak(item.id, item.answer)}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand hover:border-brand"
                      >
                        {speakingId === item.id ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                        {speakingId === item.id ? "Stop" : "Listen"} <ChevronRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-brand-muted">
          Answers are educational and drawn from reviewed Ayurveda content — not a substitute for professional
          medical advice.
        </p>
      </div>
    </section>
  );
}
